import { useRef, type ComponentProps, type ReactNode } from "react";
import { ChevronDown, Pencil, Plus } from "lucide-react";
import {
  CredentialCategory,
  CredentialRequirement,
  CredentialType,
} from "@api/shared-runtime/enums";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { blankCredentialInstance } from "../lib/blank-credential-instance";
import type { Credential } from "../types";
import { CredentialRow } from "./credential-card/credential-row";
import { CredentialStatusColumn } from "./credential-card/credential-status-column";
import { CredentialTypeBlock } from "./credential-card/credential-type-block";

const IDENTIFICATION_OPTIONS = [
  { type: CredentialType.DriversLicense, label: "Driver’s license" },
  { type: CredentialType.Passport, label: "Passport" },
  { type: CredentialType.GovernmentId, label: "Other government ID" },
];
const IDENTIFICATION_TYPES = new Set(
  IDENTIFICATION_OPTIONS.map((option) => option.type),
);
const OVERVIEW_TYPES = new Set([
  CredentialType.PersonalInfo,
  CredentialType.Npi,
  ...IDENTIFICATION_TYPES,
]);

type ProviderIdentitySectionProps = ComponentProps<
  typeof CredentialTypeBlock
> & {
  onEditPersonalDetails?: () => void;
  personalDetailsContent?: ReactNode;
};

/** The provider sees one identity overview; every record still keeps its
 * native type, evidence, and server-computed requirement and status. */
export function ProviderIdentitySection({
  credentials,
  userId,
  readOnly = false,
  onEditCredential,
  onOpenDocument,
  onOpenVerificationEvidence,
  isOpeningDocument,
  onRemoveDocument,
  appearance = "default",
  onEditPersonalDetails,
  personalDetailsContent,
}: ProviderIdentitySectionProps) {
  const isOpeningEditor = useRef(false);
  const canEdit = !readOnly && !!onEditCredential;
  const personalDetails = credentials.filter(
    (credential) => credential.credentialType === CredentialType.PersonalInfo,
  );
  const heldPersonalDetails = personalDetails.filter(
    (credential) => credential.present,
  );
  const personalTemplate = heldPersonalDetails[0] ?? personalDetails[0];
  const npi = credentials.filter(
    (credential) => credential.credentialType === CredentialType.Npi,
  );
  const identification = credentials.filter((credential) =>
    IDENTIFICATION_TYPES.has(credential.credentialType),
  );
  const heldIdentification = identification.filter(
    (credential) => credential.present,
  );
  const missingRequiredLabels = IDENTIFICATION_OPTIONS.flatMap(({ type }) => {
    const records = identification.filter(
      (credential) => credential.credentialType === type,
    );
    if (
      records.some(
        (credential) => credential.present || credential.requirementSatisfiedBy,
      )
    ) {
      return [];
    }
    const required = records.find(
      (credential) => credential.requirement === CredentialRequirement.Required,
    );
    return required ? [required.label] : [];
  });
  const remainingGroups = new Map<CredentialType, Credential[]>();
  for (const credential of credentials) {
    if (
      credential.category !== CredentialCategory.Identity ||
      OVERVIEW_TYPES.has(credential.credentialType)
    ) {
      continue;
    }
    const group = remainingGroups.get(credential.credentialType);
    if (group) {
      group.push(credential);
    } else {
      remainingGroups.set(credential.credentialType, [credential]);
    }
  }
  const rowProps = {
    userId,
    appearance,
    readOnly,
    onEdit: onEditCredential,
    onOpenDocument,
    onOpenVerificationEvidence,
    isOpeningDocument,
    onRemoveDocument: readOnly ? undefined : onRemoveDocument,
  };
  const blockProps = { ...rowProps, onEditCredential };

  return (
    <div className="space-y-5 pb-4">
      <section aria-label="Personal details" className="space-y-3 pt-2">
        <div className="flex items-center justify-between gap-3">
          <h4
            className={cn(
              "text-xs font-semibold uppercase tracking-wide text-slate-500",
              appearance === "panel" &&
                "text-sm normal-case tracking-normal text-slate-800",
            )}
          >
            Personal details
          </h4>
          {(onEditPersonalDetails || (canEdit && personalTemplate)) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 gap-1.5 rounded-xl px-2 text-xs font-medium text-primary hover:bg-primary/5 hover:text-primary",
                appearance === "panel" &&
                  "h-9 border border-slate-300 bg-white",
              )}
              onClick={() => {
                if (onEditPersonalDetails) {
                  onEditPersonalDetails();
                } else if (personalTemplate) {
                  onEditCredential?.(personalTemplate);
                }
              }}
            >
              {personalTemplate?.present ? (
                <Pencil className="size-3.5" aria-hidden="true" />
              ) : (
                <Plus className="size-3.5" aria-hidden="true" />
              )}
              {personalTemplate?.present || onEditPersonalDetails
                ? "Edit details"
                : "Add details"}
            </Button>
          )}
        </div>
        {heldPersonalDetails.length > 0 ? (
          heldPersonalDetails.map((credential) => (
            <CredentialRow
              key={credential.id}
              credential={credential}
              {...rowProps}
            />
          ))
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className="text-sm text-slate-500">
              Legal name and personal information are not on file.
            </p>
            {personalTemplate && (
              <CredentialStatusColumn credential={personalTemplate} />
            )}
          </div>
        )}
      </section>

      {personalDetailsContent}

      {npi.length > 0 && (
        <CredentialTypeBlock credentials={npi} {...blockProps} />
      )}

      <section
        aria-label="Identification documents"
        className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/50 p-4 sm:p-5"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1.5">
            <h4 className="text-sm font-semibold text-slate-900">
              Identification documents
            </h4>
            <p className="text-xs leading-relaxed text-slate-500">
              Identification documents support the legal name in the personal
              details.
            </p>
          </div>
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 gap-1.5 rounded-xl border-slate-400 bg-white px-3 text-xs font-semibold text-primary hover:border-primary/20 hover:bg-primary/5"
                >
                  <Plus className="size-3.5" aria-hidden="true" />
                  Add identification
                  <ChevronDown className="size-3.5" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 rounded-xl border-slate-200 p-1.5"
                onCloseAutoFocus={(event) => {
                  // The newly opened editor owns focus after choosing a type.
                  if (isOpeningEditor.current) {
                    event.preventDefault();
                  }
                  isOpeningEditor.current = false;
                }}
              >
                {IDENTIFICATION_OPTIONS.map(({ type, label }) => {
                  const template = identification.find(
                    (credential) => credential.credentialType === type,
                  );
                  return (
                    <DropdownMenuItem
                      key={type}
                      disabled={!template}
                      className="cursor-pointer rounded-lg text-sm focus:bg-primary/5 focus:text-primary"
                      onSelect={() => {
                        if (!template) {
                          return;
                        }
                        isOpeningEditor.current = true;
                        onEditCredential?.(
                          blankCredentialInstance(template, "new"),
                        );
                      }}
                    >
                      {label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {heldIdentification.length > 0 ? (
          <div className="space-y-3">
            {heldIdentification.map((credential) => (
              <CredentialRow
                key={credential.id}
                credential={credential}
                {...rowProps}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            No identification documents on file.
          </p>
        )}
        {missingRequiredLabels.length > 0 && (
          <p className="text-xs font-medium leading-relaxed text-red-700">
            Required identification not on file:{" "}
            {missingRequiredLabels.join(", ")}.
          </p>
        )}
      </section>

      {[...remainingGroups.values()].map((instances) => (
        <CredentialTypeBlock
          key={instances[0].credentialType}
          credentials={instances}
          {...blockProps}
        />
      ))}
    </div>
  );
}
